import { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { isEmbeddedMode } from '../auth/embedMode';
import { AUTOLOGIN_JWT_PENDING_KEY } from '../auth/preflightAuth';
import { userManager } from '../auth/userManager';
import EmailLinkTracker from '../components/EmailLinkTracker';
import DarkLoadingSpinner from '../components/LoadingSpinner/DarkLoadingSpinner';
import { useAuth } from '../contexts/AuthContext';
import PrivateLayout from '../layouts/PrivateLayout';
import { isKeycloakLoginEnabled } from '../util/constants';
import { logger } from '../util/logger';
import { ROUTE_PATHS } from './routePaths';

/**
 * Time to wait for the V3 session bridge before showing an error.
 * If V3 never sends V3_VENDOR_SESSION (e.g. config mismatch), the user sees
 * an actionable message rather than an infinite spinner.
 */
const EMBED_SESSION_TIMEOUT_MS = 5000;

const PrivateRoute = () => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const useKeycloak = isKeycloakLoginEnabled();

  /**
   * Used only in embedded (iframe) mode: becomes true after
   * EMBED_SESSION_TIMEOUT_MS if the V3 bridge has not delivered a session.
   */
  const [embedSessionTimedOut, setEmbedSessionTimedOut] = useState(false);

  useEffect(() => {
    if (!isEmbeddedMode()) return;
    if (isAuthenticated) return;

    const timer = setTimeout(() => {
      setEmbedSessionTimedOut(true);
    }, EMBED_SESSION_TIMEOUT_MS);

    return () => clearTimeout(timer);
  }, [isAuthenticated]);

  useEffect(() => {
    // Only trigger the Keycloak redirect once auth state is confirmed (not loading)
    // and the user is definitively unauthenticated.
    if (!loading && !isAuthenticated && useKeycloak) {
      /**
       * Embedded mode: V3 is the auth owner. Never redirect to Keycloak from
       * inside the iframe — that would navigate the iframe away from the report
       * page AND trigger V2's own sso/otp/send, causing a duplicate call.
       *
       * Instead, remain on the spinner. v3SessionBridge will fire
       * V3_VENDOR_SESSION shortly after the iframe loads, apply the vendor
       * credentials, and reload the iframe with a valid isLoggedIn cookie.
       */
      if (isEmbeddedMode()) {
        logger.log('[PrivateRoute] Embedded mode — waiting for V3 session bridge');
        return;
      }

      const redirectPath = location.pathname + location.search;
      localStorage.setItem('pendingRedirectUrl', redirectPath);

      const pendingJwt = new URLSearchParams(location.search).get('autologin');
      if (pendingJwt) {
        logger.log('[PrivateRoute] Auto-login — signinRedirect with autologin param');
        sessionStorage.setItem(AUTOLOGIN_JWT_PENDING_KEY, pendingJwt);
        void userManager.signinRedirect({
          extraQueryParams: { autologin: pendingJwt, prompt: 'none' },
        });
        return;
      }

      if (sessionStorage.getItem('silentSsoSuccess')) {
        void navigate(ROUTE_PATHS.LOGIN, { replace: true });
        return;
      }

      void userManager.signinRedirect();
    }
  }, [isAuthenticated, loading, useKeycloak, location, navigate]);

  if (loading) {
    return null;
  }

  if (!isAuthenticated) {
    if (useKeycloak) {
      // Embedded: wait for V3 bridge; show error after timeout instead of
      // redirecting to Keycloak, which would break the iframe context.
      if (isEmbeddedMode()) {
        if (embedSessionTimedOut) {
          return (
            <p style={{ padding: '2rem', textAlign: 'center' }}>
              Session could not be established. Please reload the parent application.
            </p>
          );
        }
        return null;
      }

      if (sessionStorage.getItem('silentSsoSuccess')) {
        return <Navigate to={ROUTE_PATHS.LOGIN} replace />;
      }
      // Spinner stays visible while the browser is in-flight to Keycloak.
      return <DarkLoadingSpinner />;
    }
    const redirectPath = location.pathname + location.search;
    localStorage.setItem('pendingRedirectUrl', redirectPath);
    return <Navigate to={ROUTE_PATHS.LOGIN} replace />;
  }

  return (
    <PrivateLayout>
      <EmailLinkTracker />
      <Outlet />
    </PrivateLayout>
  );
};

export default PrivateRoute;
